// src/services/sanitaryReportService.js
// =============================================================================
// Frontend API wrapper for sanitary operational reports
// =============================================================================

import apiClient from "../utils/apiClient";

const DEBUG_API = String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return err?.response?.data?.message || err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;
  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitaryReportService] ${context}`, { status: e.status, code: e.code, data: e.data });
  }
  throw e;
}

function normalizeListPayload(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function buildParams(params = {}) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
}

function filenameFromDisposition(disposition, fallback) {
  const raw = String(disposition || "");
  const match = raw.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function fetchSanitaryReports(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/reports", { params: buildParams(params) });
    return normalizeListPayload(res.data, ["reports"]);
  } catch (err) {
    throwApiError("fetchSanitaryReports", err);
  }
}

export async function generateSanitaryReport(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/reports/generate", payload);
    return res.data;
  } catch (err) {
    throwApiError("generateSanitaryReport", err);
  }
}

export async function getSanitaryReport(reportId) {
  try {
    const res = await apiClient.get(`/sanitary/reports/${reportId}`);
    return res.data;
  } catch (err) {
    throwApiError("getSanitaryReport", err);
  }
}

export async function lockSanitaryReport(reportId) {
  try {
    const res = await apiClient.post(`/sanitary/reports/${reportId}/lock`, {});
    return res.data;
  } catch (err) {
    throwApiError("lockSanitaryReport", err);
  }
}

export async function regenerateSanitaryReport(reportId) {
  try {
    const res = await apiClient.post(`/sanitary/reports/${reportId}/regenerate`, {});
    return res.data;
  } catch (err) {
    throwApiError("regenerateSanitaryReport", err);
  }
}

export async function downloadSanitaryReportPdf(reportId, options = {}) {
  try {
    const res = await apiClient.get(`/sanitary/reports/${reportId}/download-pdf`, {
      params: buildParams(options),
      responseType: "blob",
    });
    const filename = filenameFromDisposition(res.headers?.["content-disposition"], `hygiene-disinfection-report-${reportId}.pdf`);
    saveBlob(new Blob([res.data], { type: "application/pdf" }), filename);
  } catch (err) {
    throwApiError("downloadSanitaryReportPdf", err);
  }
}

export async function downloadSanitaryReportExcel(reportId) {
  try {
    const res = await apiClient.get(`/sanitary/reports/${reportId}/download-excel`, { responseType: "blob" });
    const filename = filenameFromDisposition(res.headers?.["content-disposition"], `hygiene-disinfection-report-${reportId}.xlsx`);
    saveBlob(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
  } catch (err) {
    throwApiError("downloadSanitaryReportExcel", err);
  }
}

export default {
  fetchSanitaryReports,
  generateSanitaryReport,
  getSanitaryReport,
  lockSanitaryReport,
  regenerateSanitaryReport,
  downloadSanitaryReportPdf,
  downloadSanitaryReportExcel,
};


